import {useApp} from "../app.context";
import {useDrag} from "react-dnd";
import AssetImage from "./assetImage.element";
import Checkmark from "./checkmark";
import {getMetadataDisplayInfo} from "../utils/metadata.utils";
import {useNavTarget} from "./useNavTarget";
import {NavTarget} from "../utils/navTarget";

function MetadataDragLabel(props: {
    uid: string,
    id?: string,
    type: string,
    draggable(): boolean,
    values?: { [key: string]: number },
    duplicate?: boolean,
    // When provided, overrides the duplicate-only computed suffix. Lets callers
    // (e.g. the Rewards tab) show authored parameters inline regardless of dupes.
    suffix?: string
}) {
    const {getLocalizationValue, setNotification, getTypeDescriptionKey} = useApp();

    const [{isDragging}, drag] = useDrag({
        type: props.type,
        item: {
            id: props.uid,
        },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        canDrag: props.draggable()
    });

    const {suffix: computedSuffix, tooltip} = getMetadataDisplayInfo(
        props.id ? {id: props.id, values: props.values ?? {}} as any : undefined,
        props.duplicate ?? false,
        getLocalizationValue
    );
    const suffix = props.suffix ?? computedSuffix;

    // Ctrl/Cmd-click navigates to this metadata's editor.
    const navTarget: NavTarget = {kind: 'metadata', uid: props.uid};

    return (
        <div ref={drag} {...useNavTarget(navTarget)} style={{opacity: isDragging || !props.draggable() ? 0.5 : 1}} title={tooltip}>
            <div className={'layout horizontal center stretch gap label'}>
                <div className={'layout horizontal gap center'}>
                    <div style={{position: 'relative'}}>
                        <AssetImage id={props.uid}/>
                        <Checkmark id={props.uid} style={{position: 'absolute', top: 2, right: 2}}/>
                    </div>
                    <p style={{width: 160, padding: 5}}
                       onClick={() => {
                           // Description is keyed by the metadata's id so all
                           // instances of the same type (e.g. all three
                           // GoldPerTurn rewards) share one editable copy —
                           // matches MetadataLabel + MetadataElement. Fall back
                           // to uid only when the caller didn't pass an id.
                           const descKey = props.id ? getTypeDescriptionKey(props.uid, props.id) : `${props.uid}.description`;
                           setNotification({
                               title: props.uid,
                               message: descKey,
                               edit: true
                           });
                       }}
                    >
                        {getLocalizationValue(props.uid)}
                    </p>
                </div>
                {suffix && <p style={{marginLeft: 'auto', padding: 5}}>{suffix.trim()}</p>}
            </div>
        </div>
    );
}

export default MetadataDragLabel;
